import { computed, reactive } from "vue";
import { invert, mapKeys, mapValues } from "lodash-es";

import { numberToLetter } from "@/helpers/utility/NumberToLetter";
import { useIndexStore } from "../index/indices.store";
import { useResourceStore } from "../resource/resources.store";
import { useTargetStore } from "../target/targets.store";
import { useFlowStore } from "../flow/flows.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import type { Uid } from "@/helpers/utility/Id";
import type { FlowBranchItem } from "../flow/FlowBranchItem";
import type { FlowItem } from "../flow/FlowItem";
import type { IndexItem } from "../index/IndexItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { TargetItem } from "../target/TargetItem";

export const useElements = () => {
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const indexStore = useIndexStore();
  const flowStore = useFlowStore();
  const flowBranchStore = useFlowBranchStore();

  type NetItemType = "target" | "resource" | "index" | "flow" | "flowBranch";
  type NetItem = TargetItem | ResourceItem | IndexItem | FlowItem | FlowBranchItem;
  type NetItemMap = {
    target: TargetItem;
    resource: ResourceItem;
    index: IndexItem;
    flow: FlowItem;
    flowBranch: FlowBranchItem;
  };

  type ByUidMap = {
    [K in NetItemType as Uid<K>]: NetItemMap[K];
  };

  const byType = computed(() => ({
    target: targetStore.items,
    resource: resourceStore.items,
    index: indexStore.items,
    flow: flowStore.items,
    flowBranch: flowBranchStore.items
  }));

  const byUid = computed(() => ({
    ...targetStore.byUid,
    ...resourceStore.byUid,
    ...indexStore.byUid,
    ...flowStore.byUid,
    ...flowBranchStore.byUid,
  } as ByUidMap));

  const ordered = computed((): NetItem[] => {
    return [
      ...targetStore.orderedItems,
      ...resourceStore.orderedItems,
      ...indexStore.orderedItems,
      ...flowStore.orderedItems,
      ...flowBranchStore.orderedItems
    ];
  });

  const elementValues = computed(() => ({
    ...targetStore.expressionValues,
    ...resourceStore.expressionValues,
    ...indexStore.expressionValues,
    ...flowStore.expressionValues,
    ...flowBranchStore.expressionValues
  }));

  const labels = computed((): Record<Uid<NetItemType>, string> => {
    const orderedElements = ordered.value;
    const alphaSortedElements = mapKeys(orderedElements, (value, key) =>
      numberToLetter(key)
    );

    return invert(
      mapValues(alphaSortedElements, (element) => element.uid)
    );
  });

  const units = computed(() => {
    const result: Record<Uid<"target" | "resource" | "index">, string> = {};

    for (const index of indexStore.orderedItems) {
      result[index.uid] = indexStore.units[index.id] ?? "";
    }
    for (const target of targetStore.orderedItems) {
      result[target.uid] = target.unit;
    }
    for (const resource of resourceStore.orderedItems) {
      result[resource.uid] = resource.unit;
    }

    return result;
  });

  const storeByType = computed(() => ({
    target: targetStore,
    resource: resourceStore,
    index: indexStore,
    flow: flowStore,
    flowBranch: flowBranchStore
  }));

  const byTitle = computed(() => {
    const result: Record<string, Uid<NetItemType>[]> = {};

    const elements = [
      ...targetStore.orderedItems,
      ...resourceStore.orderedItems,
      ...indexStore.orderedItems
    ];

    for (const item of elements) {
      const title = item.title || "";
      if (!result[title]) {
        result[title] = [];
      }
      result[title].push(item.uid);
    }

    return result;
  });

  return reactive({
    labels,
    units,
    ordered,
    byUid,
    elementValues,
    byType,
    storeByType,
    byTitle
  });
};